import React, { useMemo } from 'react';

import { css, cx } from '@emotion/css';
import { Typography } from '@mui/material';
import { useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles.ts';
import { MedicationSchema } from '@/models/ConditionModel.ts';
import { HomeServiceProvider } from '@/models/HomeServiceProviderModel.ts';
import { userFormattedName } from '@/models/UserModel.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { DropDown } from '@/uiKit/molecules/ControlledDropDown.tsx';

export type HealthCareProviderSelectionFieldProps = {
  className?: string;
  providers: HomeServiceProvider[];
};

export const HealthCareProviderSelectionField: React.FC<
  HealthCareProviderSelectionFieldProps
> = ({ providers, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const insulinScheme = useWatch({
    name: 'insulinScheme',
  }) as MedicationSchema | undefined;
  const hspError = useFormState({
    name: 'homeServiceProviderId',
  }).errors.homeServiceProviderId;

  const choices = useMemo(() => {
    const providerChoices = providers.map(value => ({
      displayName: userFormattedName(value.hspFirstname, value.hspLastname),
      value: value.hspId,
    }));
    providerChoices.push({
      displayName: t('diabetesForms.otherHSP'),
      value: 'other',
    });
    return providerChoices;
  }, [providers, t]);

  if (insulinScheme !== 'insulinPump') {
    return null;
  }

  return (
    <div className={cx(styles.container, className)}>
      <Typography variant="subtitle">{t('diabetesForms.hsp')}</Typography>
      <div>
        <DropDown
          id="tls-duration-pick"
          classes={{
            dropdown: styles.dropdown,
          }}
          fieldName="homeServiceProviderId"
          choices={choices}
        />
        {hspError?.message && typeof hspError.message === 'string' ? (
          <Typography color="error" variant="caption">
            {t(hspError.message)}
          </Typography>
        ) : null}
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(12),
    paddingBottom: theme.spacing(12),
  }),
  dropdown: css({
    width: '100%',
  }),
});
